import { useParams } from 'react-router-dom';
import {
  type ChangeEvent,
  type SyntheticEvent,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
//
import {
  Box,
  Card,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
} from '@mui/material';
import { Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import BookingsTableToolbar from '@/features/bookings-management/components/bookings/list/BookingsTableToolbar';
import { ProviderJobsTableRow } from '..';
// api
import { getSettingsData } from '@/features/app/api';
import { getUserJobs } from '@/features/users/shared/api';
// hookes + utils
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useTabs from '@/hooks/useTabs';
import {
  PROVIDER_JOBS_TABLE_HEAD,
  applySortFilterProviderJobs,
} from '../../utils';

const PROVIDER_JOBS_FILTER_TABS = [
  { value: 'previous', label: 'Previous' },
  { value: 'today', label: 'Today' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function ProviderJobs() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { userId } = useParams();
  const { errorMsg } = useSnackbarMsg();

  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [filterServiceId, setFilterServiceId] = useState<number>(0);
  const [filterServiceTypeId, setFilterServiceTypeId] = useState<number>(0);
  const [filterStartDate, setFilterStartDate] = useState<string | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<string | null>(null);
  const [filterStatusId, setFilterStatusId] = useState<number>(0);
  const [filterPaymentStatusId, setFilterPaymentStatusId] = useState<number>(0);
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } =
    useTabs('today');

  // =================
  //      QUERIES
  // =================
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getSettingsData(),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data`);
    },
  });

  const { data: providerJobs, isLoading: isJobsDataLoading } = useQuery({
    queryKey: ['providerJobs', userId],
    queryFn: () => getUserJobs(Number(userId)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching provider jobs`);
    },
  });

  const servicesWithTheirVariants = useMemo(
    () =>
      settingsData?.services?.map((service) => ({
        id: service.id,
        name: service.name,
        variants: settingsData.serviceVariants.reduce<number[]>(
          (variantIds, variant) => {
            if (variant.service_id === service.id) {
              variantIds.push(variant.id);
            }
            return variantIds;
          },
          []
        ),
      })) ?? [],
    [settingsData]
  );

  //
  const dataFiltered = applySortFilterProviderJobs({
    tableData: providerJobs || [],
    comparator: getComparator(order, orderBy),
    searchFieldValue,
    filterStatus,
    filterServiceId,
    filterServiceTypeId,
    filterStartDate,
    filterEndDate,
    filterStatusId,
    filterPaymentStatusId,
    servicesWithTheirVariants,
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound =
    (!dataFiltered.length && !!searchFieldValue) || !dataFiltered.length;

  const resetFilters = () => {
    setSearchFieldValue('');
    setFilterServiceId(0);
    setFilterServiceTypeId(0);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterStatusId(0);
    setFilterPaymentStatusId(0);
    setPage(0);
  };

  const handleSearchField = (value: string) => {
    setSearchFieldValue(value);
    setPage(0);
  };

  const handleFilterDropdown = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    switch (name) {
      case 'status':
        setFilterStatusId(Number(value));
        break;
      case 'payment_status':
        setFilterPaymentStatusId(Number(value));
        break;
      case 'service':
        setFilterServiceId(Number(value));
        setFilterServiceTypeId(0);
        break;
      case 'service_type':
        setFilterServiceTypeId(Number(value));
        break;
      default:
        break;
    }

    setPage(0);
  };

  const handleDatepickerChange = (date: string | null, dateType: string) => {
    if (dateType === 'start') {
      setFilterStartDate(date);
    }

    if (dateType === 'end') {
      setFilterEndDate(date);
    }

    setPage(0);
  };

  const handleFilterStatus = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    onChangeFilterStatus(event, newValue);
    setPage(0);
  };

  return (
    <Card>
      {isJobsDataLoading ? (
        <SkeletonLoadingTable />
      ) : (
        <>
          <Tabs
            allowScrollButtonsMobile
            variant="scrollable"
            scrollButtons="auto"
            value={filterStatus}
            onChange={handleFilterStatus}
            sx={{ px: 2, bgcolor: 'background.neutral' }}
          >
            {PROVIDER_JOBS_FILTER_TABS.map((tab) => (
              <Tab
                disableRipple
                key={tab.value}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>

          <Divider />

          <BookingsTableToolbar
            filterService={filterServiceId}
            filterServiceType={filterServiceTypeId}
            filterSearchBar={searchFieldValue}
            filterStartDate={filterStartDate}
            filterEndDate={filterEndDate}
            filterStatusId={filterStatusId}
            filterPaymentStatusId={filterPaymentStatusId}
            handleFilterDropdown={handleFilterDropdown}
            handleSearchField={handleSearchField}
            handleDatepickerChange={handleDatepickerChange}
            resetFilters={resetFilters}
            settingsData={settingsData}
            showStatusFilters
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={PROVIDER_JOBS_TABLE_HEAD}
                  rowCount={dataFiltered.length}
                  numSelected={selected.length}
                  onSort={onSort}
                />
                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((job) => (
                      <ProviderJobsTableRow key={job.id} row={job} />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      dataFiltered.length
                    )}
                  />

                  <TableNoData isNotFound={isNotFound} title="No jobs found" />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </>
      )}
    </Card>
  );
}
